/**
 * ProductDetailPage shared types (S1.4) + the S1.2 five-tab shell model.
 *
 * S1.2 (plan §S1.2, ux-lens-pinned) — the 5-tab IA GROUPS the existing section
 * registry; it does not replace it (Q-M7 default):
 * - `DETAIL_TAB_KEYS` is the pinned rail order 基本資料 → 採購與供應 → 銷售與定價
 *   → 財務資料 → 異動日誌 (tab-ia recheck; tab 2 uses the S0.2 sanctioned
 *   採購與供應 pair — the drawn label carried a banned term, see
 *   ofcVocabulary.ts `purchasingSupply`).
 * - `TAB_SECTION_GROUPS` partitions `DETAIL_SECTION_KEYS`: every section lives in
 *   exactly ONE tab (unit-tested in `__tests__/tabDeepLinks.test.tsx`).
 *   `commercial` mounts wholly under 財務資料 for now — the SALES/ACCOUNTING row
 *   split lands in S2.2 as a data edit here.
 * - Single nav axis: the Sider menu is the 5 tab groups. `?tab=` is the ONLY
 *   param the UI writes (`buildDetailTabSearchParams`); legacy `?section=` is a
 *   READ-ONLY resolver (`resolveDetailTab`) mapping old deep-links to owning tab
 *   + anchor scroll — no control ever writes it.
 *
 * The extended section registry moved here from `index.tsx` (the SR4.1 wiring
 * comment deferred that move to a slice owning this file — S1.2 owns both).
 */

export type ProductDetailSectionKey =
  | 'main'
  | 'classification'
  | 'descriptions'
  | 'commercial'
  | 'vendors'
  | 'scn'
  | 'transactions'
  | 'audit';

export const PRODUCT_DETAIL_SECTION_KEYS: readonly ProductDetailSectionKey[] = [
  'main',
  'classification',
  'descriptions',
  'commercial',
  'vendors',
  'scn',
  'transactions',
  'audit',
];

export type ProductEditModalKey = 'main' | 'classification' | 'descriptions' | 'commercial';

/** Full section registry — S1.4 base keys + the SR4.1 reorder/Item360 sections. */
export type DetailSectionKey =
  | ProductDetailSectionKey
  | 'images'
  | 'planning'
  | 'itemLocations'
  | 'salesAnalytics'
  | 'pairingRules'
  | 'controlCenter';

export const DETAIL_SECTION_KEYS: readonly DetailSectionKey[] = [
  'main',
  'classification',
  'descriptions',
  'images',
  'commercial',
  'planning',
  'vendors',
  'pairingRules',
  'itemLocations',
  'salesAnalytics',
  'transactions',
  'controlCenter',
  'scn',
  'audit',
];

/** The 5-tab rail, pinned order (tab-ia recheck; EN build-defaults Q-M6). */
export type ProductDetailTabKey =
  | 'basicInfo'
  | 'purchasingSupply'
  | 'salesPricing'
  | 'financialInfo'
  | 'changeLog';

export const DETAIL_TAB_KEYS: readonly ProductDetailTabKey[] = [
  'basicInfo',
  'purchasingSupply',
  'salesPricing',
  'financialInfo',
  'changeLog',
];

export const DEFAULT_DETAIL_TAB: ProductDetailTabKey = 'basicInfo';

/**
 * Tab → sections composition (plan §3), in board order within each tab.
 * Partition invariant: union == DETAIL_SECTION_KEYS, pairwise disjoint.
 * - salesPricing gains `pricing` (S5.1 mount) and the commercial SALES rows
 *   (S2.2 split) later; until then it carries salesAnalytics only.
 * - financialInfo carries `commercial` wholly until the S2.2 row split.
 * - changeLog: audit + transactions (build-default; Q-M5).
 * - `pairingRules` (#2168, G-2166-K-04) sits directly after `vendors` — the
 *   position `PAIRING_RULES_MOUNT` declares — so the section is an ordinary
 *   registry member: one anchor mechanism, one deep-link resolver, no splice.
 * - `images` (V3-S2, 2026-08-27 §6 ruling) sits directly after `descriptions`
 *   under 基本資料 — the mock-first product-image gallery seat.
 */
export const TAB_SECTION_GROUPS: Record<ProductDetailTabKey, readonly DetailSectionKey[]> = {
  basicInfo: ['main', 'classification', 'descriptions', 'images', 'vendors', 'pairingRules', 'scn'],
  purchasingSupply: ['planning', 'itemLocations', 'controlCenter'],
  salesPricing: ['salesAnalytics'],
  financialInfo: ['commercial'],
  changeLog: ['audit', 'transactions'],
};

/** Owning tab per section — derived from the one-tab-per-section partition. */
export const sectionOwningTab = (section: DetailSectionKey): ProductDetailTabKey => {
  const owner = DETAIL_TAB_KEYS.find((tab) => TAB_SECTION_GROUPS[tab].includes(section));
  // Partition invariant guarantees an owner; fall back defensively for safety.
  return owner ?? DEFAULT_DETAIL_TAB;
};

export interface DetailTabResolution {
  tab: ProductDetailTabKey;
  /** Section to anchor-scroll to (legacy `?section=` deep-links); null = none. */
  anchor: DetailSectionKey | null;
}

const isTabKey = (value: string | null): value is ProductDetailTabKey =>
  value != null && (DETAIL_TAB_KEYS as readonly string[]).includes(value);

const isSectionKey = (value: string | null): value is DetailSectionKey =>
  value != null && (DETAIL_SECTION_KEYS as readonly string[]).includes(value);

/**
 * READ-ONLY resolver for the route params (ux-lens pin):
 * - valid `?tab=` wins; a co-present legacy `?section=` only anchors when it
 *   belongs to that tab (never re-routes a explicit tab choice);
 * - else a valid legacy `?section=` resolves to its owning tab + anchor scroll
 *   (regression contract: `?section=itemLocations` lands 採購與供應, scrolled);
 * - else the default tab, no anchor.
 * The UI never writes `?section=` — see buildDetailTabSearchParams.
 */
export const resolveDetailTab = (
  tabParam: string | null,
  sectionParam: string | null
): DetailTabResolution => {
  if (isTabKey(tabParam)) {
    const anchor =
      isSectionKey(sectionParam) && sectionOwningTab(sectionParam) === tabParam
        ? sectionParam
        : null;
    return { tab: tabParam, anchor };
  }
  if (isSectionKey(sectionParam)) {
    return { tab: sectionOwningTab(sectionParam), anchor: sectionParam };
  }
  return { tab: DEFAULT_DETAIL_TAB, anchor: null };
};

/**
 * The ONLY search-params writer on the detail page. Emits `?tab=` alone (clean
 * URL for the default tab) — writing a tab drops any legacy `?section=`.
 */
export const buildDetailTabSearchParams = (tab: ProductDetailTabKey): Record<string, string> =>
  tab === DEFAULT_DETAIL_TAB ? {} : { tab };

/** DOM id for a section's anchor wrapper (legacy deep-link scroll target). */
export const sectionAnchorId = (section: DetailSectionKey): string =>
  `product-section-${section}`;

/**
 * Cross-surface hand-off link into one product-detail section (DemandPoolPage
 * `?section=vendors` precedent). The detail page itself still never WRITES
 * `?section=` — other surfaces hand off with it and `resolveDetailTab` reads
 * it. Typed on `DetailSectionKey`, so a link can only name a section that is
 * actually in the registry — an unresolvable deep link cannot be authored.
 */
export const productSectionDeepLink = (productId: string, section: DetailSectionKey): string =>
  `/auth/products/${productId}?section=${section}`;
